// ─────────────────────────────────────────────────────────────────────────────
// services/printJobService.ts – Firestore Print Job Service
//
// Provides:
//   - createPrintJob()        Create a new 'pending' job from the dashboard
//   - subscribeToJobs()       Live-Abo der ERSTEN Seite (onSnapshot, REQ-OPS-033)
//   - fetchJobsPage()         Nachladen einer weiteren, statischen Seite (Cursor)
//   - subscribeToRegisters()  Real-time listener for register statuses
//   - countJobStats()         Serverseitige Zaehlung je Mandant (REQ-OPS-034)
//   - countPendingForPrinter()Serverseitige Zaehlung je Drucker (REQ-OPS-034)
//   - countHeldForPrinter()   Die zurueckgehaltene Teilmenge (REQ-LC-015)
//
// Ablage: ausschliesslich die top-level Collection `printJobs`
// (PRINT_JOBS_COLLECTION, REQ-DM-015/015a). Die Mandantentrennung laeuft ueber
// den Abfragefilter `companyId` — sie steckte frueher im Collection-Pfad einer
// Subcollection, die kein Erzeuger je befuellt hat (OP-40).
// ─────────────────────────────────────────────────────────────────────────────

import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  serverTimestamp,
  type FirestoreError,
  type Unsubscribe,
  type QuerySnapshot,
  type QueryDocumentSnapshot,
  type DocumentData,
  type QueryConstraint,
} from 'firebase/firestore';

import { db, auth }   from '@/firebase/config';
// Fehlerweg, Protokoll und Rueckzugsstaffel der Live-Abos liegen an EINER
// Stelle (REQ-OPS-053 (e), T-M21.4) — auch fuer diesen Dienst, der bewusst
// keinen Store kennt.
import { liveAbo, type LiveAboFehlerLage } from '@/utils/liveAbo';
import { JOBS_PAGE_SIZE } from '@/utils/jobPagination';
import {
  COUNTED_JOB_STATUSES,
  HELD_EQUALITY_VALUE,
  isHeldJob,
  type JobStatCounts,
} from '@/utils/jobCounts';
import {
  PRINT_JOBS_COLLECTION,
  NEW_JOB_STATUS,
  NEW_JOB_HOLD_UNTIL_READY,
} from '@/firebase/collections';
import type {
  PrintJob,
  Register,
  PayfixPayload,
} from '@/firebase/models';
import { PrintJobStatus } from '@/firebase/models';

// ── Helper ────────────────────────────────────────────────────────────────────

/**
 * Safely map a Firestore doc snapshot to a typed PrintJob.
 * Returns null and logs a warning if required fields are missing.
 */
function toPrintJob(snap: DocumentData): PrintJob | null {
  const data = snap.data() as Record<string, unknown>;
  if (!data) return null;
  // Validate required fields
  if (!data['status'] || !data['companyId'] || !data['printerId'] || !data['createdAt']) {
    console.warn('[PrintJobService] Skipping doc with missing required fields:', snap.id, data);
    return null;
  }
  return { id: snap.id, ...data } as PrintJob;
}

/** Map a Firestore doc snapshot to a typed Register. */
function toRegister(snap: DocumentData): Register {
  return { id: snap.id, ...snap.data() } as Register;
}

// ── Create ────────────────────────────────────────────────────────────────────

export interface CreatePrintJobParams {
  registerId: string;
  printerId:  string;
  companyId:  string;
  /**
   * Zugeordneter Druckserver (REQ-DM-030, denormalisiert). PFLICHT: ein
   * `pending`-Auftrag ohne `printserverId` faellt durch die Zustell-Abfrage des
   * Daemons (REQ-LC-010) und bliebe stumm liegen.
   */
  printserverId: string;
  locationId?: string;
  payload:    PayfixPayload;
}

/**
 * Create a new PrintJob document in Firestore with status 'pending'.
 * This immediately triggers the Raspberry Pi's onSnapshot listener.
 *
 * Ablage: top-level `printJobs` (REQ-DM-015a) — dieselbe Collection, die der
 * `payfixwebhook` befuellt und der Druckserver abhoert.
 *
 * @returns The newly created job's Firestore document ID.
 */
export async function createPrintJob(params: CreatePrintJobParams): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Nicht authentifiziert.');

  const jobsCol = collection(db, PRINT_JOBS_COLLECTION);
  const data: Record<string, unknown> = {
    registerId:    params.registerId,
    printerId:     params.printerId,
    printserverId: params.printserverId,
    companyId:     params.companyId,
    payload:       params.payload,
    status:        NEW_JOB_STATUS,
    // Halte-Kennzeichen: ohne Bedingung `false` (REQ-LC-009 Praez. 1 (c)).
    // Ein aus der Verwaltung angelegter Auftrag hat wie der Nachdruck keinen
    // Produzenten der Freigabe; ohne das Feld waere er fuer den Zustell-
    // Listener unsichtbar (REQ-LC-013) und verfiele stumm nach vier Stunden.
    holdUntilReady: NEW_JOB_HOLD_UNTIL_READY,
    source:        'dashboard',
    createdAt:     serverTimestamp(),
    updatedAt:     serverTimestamp(),
  };
  if (params.locationId) data['locationId'] = params.locationId;

  const docRef = await addDoc(jobsCol, data);

  return docRef.id;
}

// ── Real-time Subscriptions ───────────────────────────────────────────────────

/**
 * Undurchsichtiger Cursor einer Seite: das LETZTE Dokument der Seite. Er wird
 * unveraendert an `startAfter(...)` zurueckgegeben; der Store liest ihn nicht.
 */
export type JobsPageCursor = QueryDocumentSnapshot<DocumentData>;

/** Eine geladene Seite der Auftragsliste (REQ-OPS-033). */
export interface JobsPage {
  /** Die lesbaren Auftraege der Seite, in Abfragereihenfolge. */
  jobs:   PrintJob[];
  /** Cursor fuer die naechste Seite; `null`, wenn die Seite leer ist. */
  cursor: JobsPageCursor | null;
  /**
   * ROHE Dokumentzahl der Seite — nicht `jobs.length`. Nur sie entscheidet, ob
   * es weitere Seiten geben kann: ein wegen fehlender Pflichtfelder verworfenes
   * Dokument zaehlt fuer Firestore trotzdem gegen das `limit`.
   */
  size:   number;
}

/**
 * Gemeinsame Abfrageform der Auftragsliste — EINE Stelle fuer Live-Seite und
 * Nachladen (REQ-DM-050/050a).
 *
 * Form: `companyId` (+ optional `status`) als Gleichheitsfilter, danach
 * `orderBy('createdAt','desc')`. Die Sortierrichtung ist Teil des Vertrags: ein
 * Index mit richtiger Feldmenge, aber falscher Richtung bedient die Abfrage
 * nicht. Cursor-Pagination aendert die Abfrageform NICHT, sondern nur ihren
 * Startpunkt — es braucht deshalb keinen neuen Composite-Index.
 */
function jobsQueryConstraints(
  companyId: string,
  statusFilter: PrintJobStatus | 'all',
): QueryConstraint[] {
  const constraints: QueryConstraint[] = [where('companyId', '==', companyId)];
  if (statusFilter !== 'all') constraints.push(where('status', '==', statusFilter));
  constraints.push(orderBy('createdAt', 'desc'));
  return constraints;
}

/** Snapshot einer Seitenabfrage in das Seitenergebnis uebersetzen. */
function toJobsPage(snapshot: QuerySnapshot): JobsPage {
  const docs = snapshot.docs;
  return {
    jobs:   docs.map(toPrintJob).filter((j): j is PrintJob => j !== null),
    cursor: docs.length ? (docs[docs.length - 1] as JobsPageCursor) : null,
    size:   docs.length,
  };
}

export interface SubscribeToJobsOptions {
  companyId:  string;
  statusFilter?: PrintJobStatus | 'all';
  /** Seitengroesse; Vorgabe `JOBS_PAGE_SIZE` (REQ-OPS-033). */
  pageSize?:  number;
  onData:     (page: JobsPage) => void;
  /**
   * Der Fehlerweg des Aufrufers (REQ-OPS-053 (a) (ii)). Die zweite Angabe sagt,
   * ob eine Wiederaufnahme folgt oder ob die Staffel erschoepft ist — der
   * Aufrufer muss dafuer nicht selbst zaehlen.
   */
  onError?:   (err: FirestoreError, lage: LiveAboFehlerLage) => void;
  /**
   * Der Rueckweg: erster erfolgreicher Schnappschuss NACH einem Fehler. Hier
   * nimmt der Aufrufer seinen Fehlzustand zurueck (REQ-OPS-053 (a) in der
   * Gegenrichtung). Beim ersten Schnappschuss eines gesunden Abos kommt er
   * NICHT — gezaehlt wird allein in `utils/liveAbo.ts`.
   */
  onRecovered?: () => void;
  /** Der stille Fall: Abmeldung, kein Fehlzustand (REQ-OPS-053 (c)). */
  onStill?:   () => void;
}

/**
 * Live-Abo der ERSTEN Seite der Auftragsliste (REQ-OPS-033, REQ-PS-021).
 *
 * Live ist bewusst NUR die erste Seite: dort kommen neue Auftraege herein, und
 * dort ist die laufende Aktualisierung der Zweck. Nachgeladene Seiten sind
 * Cursor-Abfragen und stehen still (`fetchJobsPage`). Ein `onSnapshot` ueber
 * alle geladenen Seiten muesste bei jedem eintreffenden Auftrag die
 * Seitengrenzen neu bestimmen — genau dort entstehen Doppel- und Fehlanzeigen.
 *
 * Das `limit` ist die SEITENgroesse, keine Kuerzungsgrenze: was dahinter liegt,
 * wird nachgeladen, nicht abgeschnitten.
 *
 * Das `onSnapshot(` selbst steht seit dem 2026-08-18 (T-M21.4) in
 * `utils/liveAbo.ts`: DIE LIVE-SEITE HAENGT UNVERAENDERT AN EINEM LIVE-ABO,
 * das Nachladen (`fetchJobsPage`) ausdruecklich NICHT. Mitgekommen sind der
 * Fehlerweg, das Protokoll und die Rueckzugsstaffel (REQ-OPS-053).
 *
 * Ablage: top-level `printJobs` (REQ-DM-015a); die Mandantentrennung laeuft
 * ueber den Filter `companyId` (REQ-DM-050: Index `companyId + createdAt DESC`
 * bzw. `companyId + status + createdAt DESC`).
 *
 * @returns Unsubscribe function – call it to stop listening (e.g., on unmount).
 */
export function subscribeToJobs(opts: SubscribeToJobsOptions): Unsubscribe {
  const {
    companyId,
    statusFilter = 'all',
    pageSize     = JOBS_PAGE_SIZE,
    onData,
    onError,
    onRecovered,
    onStill,
  } = opts;

  const q = query(
    collection(db, PRINT_JOBS_COLLECTION),
    ...jobsQueryConstraints(companyId, statusFilter),
    limit(pageSize),
  );

  return liveAbo<QuerySnapshot>({
    collection: PRINT_JOBS_COLLECTION,
    companyId,
    ziel: () => q,
    onData: (snapshot) => onData(toJobsPage(snapshot)),
    onFehler: (err, lage) => {
      console.error('[PrintJobService] Firestore listener error:', err);
      onError?.(err, lage);
    },
    onErholt: () => onRecovered?.(),
    onStill:  () => onStill?.(),
  });
}

export interface FetchJobsPageOptions {
  companyId:     string;
  statusFilter?: PrintJobStatus | 'all';
  /** Cursor der zuletzt geladenen Seite; ohne ihn waere es wieder Seite 1. */
  startAfterDoc: JobsPageCursor;
  pageSize?:     number;
}

/**
 * Eine WEITERE Seite nachladen (REQ-OPS-033).
 *
 * Bewusst `getDocs` und nicht `onSnapshot`: nachgeladene Seiten stehen still.
 * Bewusst Cursor (`startAfter`) und nicht ein wachsendes `limit`: so bleibt die
 * Zahl der je Abfrage gelesenen Dokumente beschraenkt, statt mit jeder Seite
 * die ganze Liste erneut zu lesen.
 */
export async function fetchJobsPage(opts: FetchJobsPageOptions): Promise<JobsPage> {
  const {
    companyId,
    statusFilter = 'all',
    startAfterDoc,
    pageSize     = JOBS_PAGE_SIZE,
  } = opts;

  const q = query(
    collection(db, PRINT_JOBS_COLLECTION),
    ...jobsQueryConstraints(companyId, statusFilter),
    startAfter(startAfterDoc),
    limit(pageSize),
  );

  return toJobsPage(await getDocs(q));
}

/**
 * REQ-DM-004: Abonniert die logische Warteschlange GENAU EINES Druckers.
 *
 * Query-Form exakt nach Spec: alle Auftraege mit dessen `printerId` und
 * `status == 'pending'`, sortiert nach `createdAt` aufsteigend. Kein
 * gemeinsames Band je Standort oder je Druckserver.
 *
 * Ablage: top-level `printJobs` (REQ-DM-015a) — dieselbe Warteschlange, die
 * der Druckserver abarbeitet; die Ansicht zeigt damit den tatsaechlichen
 * Rueckstand des Geraets. Der Mandant kommt als Filter `companyId` hinzu.
 * Composite-Index (REQ-DM-050) in firebase/firestore.indexes.json:
 * `companyId (ASC) + printerId (ASC) + status (ASC) + createdAt (ASC)`.
 *
 * @returns Unsubscribe function.
 */
export function subscribeToPrinterQueue(
  companyId: string,
  printerId: string,
  onData: (jobs: PrintJob[]) => void,
  onError?: (err: FirestoreError, lage: LiveAboFehlerLage) => void,
  onStill?: () => void,
  /** Erster erfolgreicher Schnappschuss NACH einem Fehler (REQ-OPS-053 (a)). */
  onRecovered?: () => void,
): Unsubscribe {
  const q = query(
    collection(db, PRINT_JOBS_COLLECTION),
    where('companyId', '==', companyId),
    where('printerId', '==', printerId),
    where('status', '==', PrintJobStatus.PENDING),
    orderBy('createdAt', 'asc'),
  );

  return liveAbo<QuerySnapshot>({
    collection: PRINT_JOBS_COLLECTION,
    companyId,
    printerId,
    ziel: () => q,
    onData: (snapshot) => {
      const jobs = snapshot.docs
        .map(toPrintJob)
        .filter((j): j is PrintJob => j !== null);
      onData(jobs);
    },
    onFehler: (err, lage) => {
      console.error('[PrintJobService] Printer-Queue listener error:', err);
      onError?.(err, lage);
    },
    onErholt: () => onRecovered?.(),
    onStill:  () => onStill?.(),
  });
}

// ── Serverseitige Zaehlungen (REQ-OPS-034) ───────────────────────────────────
//
// Sie stehen bewusst NEBEN den Abfragen, die sie spiegeln: dieselben Filter,
// nur ohne Sortierung, ohne `limit` und ohne Abo. Was hier gezaehlt wird, ist
// unabhaengig davon, wie viel die Liste gerade geladen hat — genau das ist der
// Zweck (Befund OP-72: die Kennzahlen lasen das 50er-Live-Fenster mit).
//
// INDEX-LAGE (geprueft, nicht zugesagt): eine Aggregation nutzt dieselben
// Indizes wie die zugrundeliegende Abfrage, und eine Abfrage OHNE `orderBy`
// wird von jedem Index bedient, dessen Praefix ihre Gleichheitsfelder enthaelt.
// `companyId` + `status` ist Praefix von `companyId, status, createdAt DESC`;
// `companyId` + `printerId` + `status` ist Praefix von `companyId, printerId,
// status, createdAt ASC`; `companyId` allein deckt der automatische
// Einzelfeld-Index. Der Nachweis liegt statisch in
// `dashboard/test/firestoreIndexes.test.ts` (REQ-DM-050a), nicht in einer
// gruenen Emulator-Suite: der Emulator erzwingt Indizes nicht.
//
// ⚠ BERICHTIGT AM 2026-08-12 (T-M15.6, REQ-OPS-036 Praez. 6 — der bisherige
// Stand bleibt als datierte Aussage stehen, statt geloescht zu werden):
// *bis dahin schloss dieser Block mit „Es entsteht KEIN neuer Index".* Das galt
// fuer die DREI Zaehlformen darueber und gilt fuer sie unveraendert. Es gilt
// NICHT mehr allgemein: die zwei Zaehlungen der GEHALTENEN Menge (REQ-LC-015)
// nehmen eine VIERTE Gleichheitsbedingung auf (`holdUntilReady`), und die
// Praefix-Regel war damit von keinem bestehenden Eintrag mehr erfuellt. Zwei
// Eintraege sind hinzugekommen — `printJobs: companyId ASC, status ASC,
// holdUntilReady ASC` und `printJobs: companyId ASC, printerId ASC, status ASC,
// holdUntilReady ASC` (REQ-OPS-034 Nachtrag 2026-08-12); damit gilt REQ-OPS-039
// auch hier: Index zuerst, Sichtpruefung „Aktiviert", dann die Auslieferung.
//
// ⚠ DIE RICHTUNG DER ZAEHLUNG IST VORGESCHRIEBEN (REQ-LC-015 Praez. 1 (d)):
// gezaehlt wird `holdUntilReady == true` und ABGEZOGEN. Ein direkter Filter
// `holdUntilReady == false` uebersaehe jedes Bestandsdokument ohne das Feld —
// die Zahlen wuerden ZU KLEIN. Die Begruendung im Einzelnen steht bei
// `HELD_EQUALITY_VALUE` in `utils/jobCounts.ts`, der Abzug selbst bei
// `subtractHeld` — hier wird nur gezaehlt, nie gerechnet.

/**
 * Gemeinsame Filterform der Mandanten-Zaehlungen — dieselben Gleichheitsfilter
 * wie `jobsQueryConstraints`, aber OHNE `orderBy`. Eine Aggregation braucht
 * keine Reihenfolge, und ohne sie faellt die Index-Anforderung auf den Praefix
 * der Gleichheitsfelder zurueck.
 */
function jobCountConstraints(companyId: string, status?: PrintJobStatus): QueryConstraint[] {
  const constraints: QueryConstraint[] = [where('companyId', '==', companyId)];
  if (status) constraints.push(where('status', '==', status));
  return constraints;
}

/**
 * Die Filterform des Warteschlangen-Abzeichens — dieselben drei
 * Gleichheitsfilter wie `subscribeToPrinterQueue`, ohne dessen `orderBy`.
 */
function printerPendingCountConstraints(
  companyId: string,
  printerId: string,
): QueryConstraint[] {
  return [
    where('companyId', '==', companyId),
    where('printerId', '==', printerId),
    where('status',    '==', PrintJobStatus.PENDING),
  ];
}

/**
 * Die GEHALTENE Teilmenge je Mandant (REQ-LC-015): dieselben Filter wie die
 * Kachel `pending`, dazu `holdUntilReady == true`.
 *
 * Der Wert kommt aus `HELD_EQUALITY_VALUE` und ist ein WAHRHEITSWERT — nie die
 * Zeichenkette `'true'`. Firestore vergleicht typstreng: ein falscher Typ
 * liefert ein leeres Ergebnis OHNE Fehlermeldung, die Kachel zeigte dann
 * dauerhaft null gehaltene Auftraege und niemand saehe es.
 */
function heldJobCountConstraints(companyId: string): QueryConstraint[] {
  return [
    where('companyId',      '==', companyId),
    where('status',         '==', PrintJobStatus.PENDING),
    where('holdUntilReady', '==', HELD_EQUALITY_VALUE),
  ];
}

/** Dieselbe gehaltene Teilmenge fuer GENAU EINEN Drucker (REQ-LC-015). */
function printerHeldCountConstraints(
  companyId: string,
  printerId: string,
): QueryConstraint[] {
  return [
    where('companyId',      '==', companyId),
    where('printerId',      '==', printerId),
    where('status',         '==', PrintJobStatus.PENDING),
    where('holdUntilReady', '==', HELD_EQUALITY_VALUE),
  ];
}

/** Eine Aggregation absetzen und die Zahl herausreichen. */
async function runCount(constraints: QueryConstraint[]): Promise<number> {
  const snapshot = await getCountFromServer(
    query(collection(db, PRINT_JOBS_COLLECTION), ...constraints),
  );
  return snapshot.data().count;
}

/**
 * Die fuenf Kennzahlen der Uebersicht SERVERSEITIG zaehlen (REQ-OPS-034).
 *
 * Fuenf getrennte Aggregationen statt einer Abfrage mit clientseitiger
 * Gruppierung: Firestore kennt kein `GROUP BY`, und eine Abfrage, die alle
 * Dokumente laedt, um sie zu gruppieren, waere genau der Fehler, der behoben
 * wird. Sie laufen gemeinsam (`Promise.all`) — schlaegt eine fehl, schlaegt der
 * ganze Aufruf fehl, und die Ansicht faellt sichtbar auf die als Untergrenze
 * gekennzeichnete Fensterzahl zurueck (`resolveCount`), statt eine halb
 * gezaehlte Kachelreihe zu zeigen.
 */
export async function countJobStats(companyId: string): Promise<JobStatCounts> {
  const [total, held, ...perStatus] = await Promise.all([
    runCount(jobCountConstraints(companyId)),
    // Die GEHALTENE Menge (REQ-LC-015) — sie laeuft im selben `Promise.all`
    // wie die uebrigen: entweder ist die ganze Kachelreihe gezaehlt, oder
    // keine. Eine Reihe, in der `pending` gezaehlt und `zurueckgehalten`
    // geschaetzt waere, liesse sich nicht auseinanderhalten.
    runCount(heldJobCountConstraints(companyId)),
    ...COUNTED_JOB_STATUSES.map(s => runCount(jobCountConstraints(companyId, s))),
  ]);

  const [pending, printing, completed, failed] = perStatus;
  return {
    total:       total     ?? 0,
    // UNGESCHMAELERT: der Abzug geschieht an genau einer Stelle
    // (`subtractHeld` in `utils/jobCounts.ts`), nicht hier.
    pending:     pending   ?? 0,
    pendingHeld: held      ?? 0,
    printing:    printing  ?? 0,
    completed:   completed ?? 0,
    failed:      failed    ?? 0,
  };
}

/**
 * Die wartenden Auftraege GENAU EINES Druckers zaehlen (REQ-OPS-034, REQ-DM-004).
 *
 * Dieselben Filter wie `subscribeToPrinterQueue` — diese Abfrage bleibt
 * unveraendert, sie traegt gar kein `limit` und war nie falsch. Falsch war das
 * ABZEICHEN daneben, das seine Zahl aus dem Live-Fenster des Stores nahm.
 */
export async function countPendingForPrinter(
  companyId: string,
  printerId: string,
): Promise<number> {
  return runCount(printerPendingCountConstraints(companyId, printerId));
}

/**
 * Die ZURUECKGEHALTENEN Auftraege genau eines Druckers (REQ-LC-015).
 *
 * Sie wird nur an Kanaelen abgesetzt, die ueberhaupt halten koennen — siehe die
 * Begruendung bei `countPendingForPrinters`.
 */
export async function countHeldForPrinter(
  companyId: string,
  printerId: string,
): Promise<number> {
  return runCount(printerHeldCountConstraints(companyId, printerId));
}

/** Ein Drucker, fuer den gezaehlt werden soll. */
export interface PrinterQueueCountRequest {
  printerId: string;
  /**
   * Kann an diesem Kanal ueberhaupt ein Auftrag zurueckgehalten werden?
   *
   * Das ist genau `kitchenTvEnabled === true` (REQ-LC-009 Bedingung (3)) — und
   * die Antwort entscheidet, ob die zweite Aggregation ueberhaupt abgesetzt
   * wird. Siehe die Begruendung an `countPendingForPrinters`.
   */
  canHold: boolean;
}

/** Was fuer einen Drucker gezaehlt wurde. */
export interface PrinterQueueCounts {
  /** Alle `pending`-Auftraege — ungeschmaelert. */
  pending: number;
  /** Davon zurueckgehalten; an einem Kanal ohne Halte-Moeglichkeit immer 0. */
  held:    number;
}

/**
 * Dieselbe Zaehlung fuer mehrere Drucker auf einmal — je Drucker die
 * Gesamtzahl UND die gehaltene Teilmenge (REQ-LC-015).
 *
 * Bewusst je Drucker eine Aggregation und keine gemeinsame Abfrage: Firestore
 * kann nicht nach `printerId` gruppieren, und eine Abfrage ueber alle
 * `pending`-Auftraege des Mandanten muesste sie clientseitig zaehlen — mit
 * genau der Fenstergrenze, die dieser Task beseitigt.
 *
 * ⚠ AUSDRUECKLICH GEBILLIGTE EINSPARUNG (REQ-LC-015 Praez. 1 (d)): die ZWEITE
 * Aggregation entfaellt an jedem Kanal ohne zugeschaltete Kitchen-TV-Ansicht.
 * Dort ist `holdUntilReady` nach REQ-LC-009 Bedingung (3) nie `true`, die
 * Abfrage koennte also nur null liefern. Ohne diese Einsparung verdoppelte sich
 * die Zahl der Aggregationen je Takt — bei einem Mandanten nahe
 * `PRINTER_LIST_LIMIT = 100` waeren das bis zu 100 WEITERE Aggregationen je
 * 30-Sekunden-Takt und geoeffneter Ansicht (OP-82).
 *
 * Einzelne Fehlschlaege werden NICHT verschwiegen: der Drucker fehlt dann
 * VOLLSTAENDIG in der Rueckgabe — auch wenn nur die zweite Aggregation
 * fehlschlug. Die Ansicht kennzeichnet seine Zahl dann als Untergrenze, statt
 * die gehaltenen stillschweigend mitzuzaehlen (REQ-LC-015) oder eine falsche
 * Null zu zeigen.
 */
export async function countPendingForPrinters(
  companyId: string,
  printers: readonly PrinterQueueCountRequest[],
): Promise<Record<string, PrinterQueueCounts>> {
  const results = await Promise.allSettled(
    printers.map(async ({ printerId, canHold }): Promise<PrinterQueueCounts> => {
      const [pending, held] = await Promise.all([
        countPendingForPrinter(companyId, printerId),
        canHold ? countHeldForPrinter(companyId, printerId) : Promise.resolve(0),
      ]);
      return { pending, held };
    }),
  );

  const counts: Record<string, PrinterQueueCounts> = {};
  results.forEach((result, i) => {
    const id = printers[i]?.printerId;
    if (id && result.status === 'fulfilled') counts[id] = result.value;
  });
  return counts;
}

/**
 * Subscribe to all Registers for a company (for status indicators).
 *
 * @returns Unsubscribe function.
 */
export function subscribeToRegisters(
  companyId: string,
  onData: (registers: Register[]) => void,
  onError?: (err: FirestoreError, lage: LiveAboFehlerLage) => void,
  onStill?: () => void,
  /** Erster erfolgreicher Schnappschuss NACH einem Fehler (REQ-OPS-053 (a)). */
  onRecovered?: () => void,
): Unsubscribe {
  const q = query(
    collection(db, 'registers'),
    where('companyId', '==', companyId),
    orderBy('name', 'asc'),
  );

  return liveAbo<QuerySnapshot>({
    collection: 'registers',
    companyId,
    ziel: () => q,
    onData: (snapshot) => {
      onData(snapshot.docs.map(toRegister));
    },
    onFehler: (err, lage) => {
      console.error('[PrintJobService] Register listener error:', err);
      onError?.(err, lage);
    },
    onErholt: () => onRecovered?.(),
    onStill:  () => onStill?.(),
  });
}

// ── Stats helpers ─────────────────────────────────────────────────────────────

export interface JobStats {
  total:     number;
  pending:   number;
  /**
   * Davon zurueckgehalten (REQ-LC-015). Die RUECKFALLZAHL folgt der Zahl, die
   * sie traegt: zoege die Kachel die gehaltenen ab und diese Untergrenze nicht,
   * waere die als „≥ n" gekennzeichnete Zahl GROESSER als die exakte daneben —
   * der Leser saehe einen Widerspruch statt einer Untergrenze
   * (REQ-LC-015 Praez. 1 (a), Zaehlstelle 5).
   */
  pendingHeld: number;
  printing:  number;
  completed: number;
  failed:    number;
}

/**
 * Derive job statistics from a list of jobs.
 *
 * Die gehaltene Menge wird auch hier POSITIV erhoben (`isHeldJob`, also
 * `=== true`) und nicht als Gegenteil gefiltert — dieselbe Richtung wie
 * serverseitig (REQ-LC-015 Praez. 1 (d)). Ein Auftrag ohne das Feld zaehlt
 * damit weiterhin als wartend.
 */
export function computeJobStats(jobs: PrintJob[]): JobStats {
  const pending = jobs.filter((j) => j.status === PrintJobStatus.PENDING);
  return {
    total:       jobs.length,
    pending:     pending.length,
    pendingHeld: pending.filter(isHeldJob).length,
    printing:    jobs.filter((j) => j.status === PrintJobStatus.PRINTING).length,
    completed:   jobs.filter((j) => j.status === PrintJobStatus.COMPLETED).length,
    failed:      jobs.filter((j) => j.status === PrintJobStatus.FAILED).length,
  };
}
